import { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getListReportByReportIds } from 'api/endpoints/children';
import RadarChart from 'components/RadarChart';
import { FORMAT_TIME } from 'constants/index';
import useChildren from 'hooks/useChildren';
import Video from 'pages/Users/Detail/_components/RecentAnswerResultsAndVideoResults/Video';
import { convertToQueryString } from 'utils/helper';

/**
 * @component RecentExerciseVideoSelection
 * @description Component for displaying the recent exercise video selection
 * @returns {React.ReactNode}
 */
const RecentExerciseVideoSelection = ({ datesHasVideo }) => {
  const { t } = useTranslation();

  const { getExerciseVideoSelection } = useChildren();

  const { me } = useSelector((state) => state.auth);
  const { childrenDetail } = useSelector((state) => state.children);

  const [listVideo, setListVideo] = useState([]);

  const isCallingApi = useRef(false);

  const getListExerciseVideoSelection = useCallback(async () => {
    if (me && childrenDetail?.id) {
      if (datesHasVideo.length > 0) {
        if (!isCallingApi.current) {
          isCallingApi.current = true;
          const response = await getExerciseVideoSelection(datesHasVideo);
          if (response) {
            setListVideo(response);
          }
        }
      }
    }
  }, [me, childrenDetail?.id, datesHasVideo]);

  useEffect(() => {
    getListExerciseVideoSelection();
  }, [getListExerciseVideoSelection]);

  return (
    <>
      {listVideo.length > 0 &&
        listVideo.map((item, index) => (
          <div className="border border-solid border-gray-651" key={`video-ase-${index}`}>
            <div className="px-4 font-bold h-9 flex items-center border-b border-solid border-gray-651">
              <p>
                {t(item.report_type === 'VIDEO' ? 'EXERCISE_RECORD_ASE' : 'EXERCISE_RECORD_DAY', {
                  date: dayjs(item.created_at).format(FORMAT_TIME.DATE),
                })}
              </p>
            </div>
            <div className="my-2 px-2 flex flex-col gap-2">
              {item.video_response_snapshot?.videos?.map(
                (item) =>
                  (item.video_url || (item.source_blob && item.source_bucket)) && (
                    <Video key={item.id} videoUrl={item.video_url} item={item} categoryName={item.video_category.name} />
                  )
              )}
            </div>
          </div>
        ))}
    </>
  );
};

const RecentEvaluationAnalysisResults = ({ datesReportRegistration }) => {
  const { t } = useTranslation();

  const [listReport, setListReport] = useState([]);

  const isCallingApi = useRef(false);

  const getListReport = useCallback(async () => {
    if (datesReportRegistration.length > 0 && !isCallingApi.current) {
      isCallingApi.current = true;
      const queryString = convertToQueryString({ report_ids: datesReportRegistration });
      const response = await getListReportByReportIds(queryString);
      setListReport(response?.data);
    }
  }, [datesReportRegistration]);

  useEffect(() => {
    getListReport();
  }, [getListReport]);

  if (datesReportRegistration.length === 0) {
    return null;
  }

  return listReport.map((item, index) => {
    const { created_at, analysis_content } = item;

    return (
      <div className="border border-solid border-gray-651" key={`report-analysis-result-${index}`}>
        <div className="px-4 font-bold min-h-9 flex items-center border-b border-solid border-gray-651">
          <p>{t('SURVEY_ANALYSIS_RESULT', { date: dayjs(created_at).format(FORMAT_TIME.DATE) })}</p>
        </div>
        <div className="mt-2">
          <div className="w-full h-50">
            {analysis_content?.analysis_results?.score_summary && (
              <RadarChart color={false} data={analysis_content?.analysis_results?.score_summary} />
            )}
          </div>
          <div className="p-4">
            {analysis_content?.analysis_results?.evaluation?.summary && (
              <div
                className="mt-6 text-black-102"
                dangerouslySetInnerHTML={{ __html: analysis_content?.analysis_results?.evaluation?.summary || '' }}
              />
            )}
          </div>
        </div>
      </div>
    );
  });
};

const RecentAnswerResultsAndVideoResults = ({ datesHasVideo, datesReportRegistration, isRecently, isReportRegistration }) => {
  const { t } = useTranslation();
  const titleRef = useRef(null);
  const { currentLanguage } = useSelector((state) => state.language);

  const [heightTitle, setHeightTitle] = useState(0);

  useEffect(() => {
    if (titleRef?.current) {
      setHeightTitle(titleRef?.current?.clientHeight);
    }
  }, [titleRef?.current?.clientHeight, currentLanguage]);

  return (
    <div className="h-full p-4 bg-white w-full">
      <p className="font-bold text-xl mb-6" ref={titleRef}>
        {isRecently
          ? t('RECENT_ANSWER_AND_VIDEO_RESULTS_RECENTLY')
          : isReportRegistration
            ? t('RECENT_ANSWER_AND_VIDEO_RESULTS_REPORT_REGISTRATION')
            : t('RECENT_ANSWER_AND_VIDEO_RESULTS')}
      </p>
      <div className="overflow-auto" style={{ height: `calc(100% - ${heightTitle + 24}px)` }}>
        {/* Video */}
        <RecentExerciseVideoSelection datesHasVideo={datesHasVideo} />

        {/* Report */}
        <RecentEvaluationAnalysisResults datesReportRegistration={datesReportRegistration} />
      </div>
    </div>
  );
};

export default RecentAnswerResultsAndVideoResults;
